/**
 * Dedicated store for WhatsApp Messages mini client panel.
 * Keeps ClientState.fetch / TaskState.list free for ClientDetails and Notes pages.
 */
import {createSlice} from "@reduxjs/toolkit";
import apiService from "../../api/apiService";
import {endPoints} from "../../api/endpoints";
import {formatAxiosErrorMessage, qargsToQstring} from "../../utils/common";
import {apiUrlBuilder} from "../../utils/util";
import {notesListPrependNote} from "../Application/Task/taskReducer";

const emptyEntity = () => ({
    isLoading: false,
    data: null,
    error: null,
});

const emptyList = () => ({
    isLoading: false,
    data: [],
    error: null,
});

const WhatsappClientPanelInitialState = {
    // Currently loaded client id — used to ignore stale responses
    activeClientId: null,
    client: emptyEntity(),
    notes: emptyList(),
    tasks: emptyList(),
    appointments: emptyList(),
    noteCreate: {
        isLoading: false,
        error: null,
    },
};

/** Normalize list payloads from /task/ or /appointments/ */
const unwrapList = (payload) => {
    const body = payload?.data ?? payload ?? null;
    if (Array.isArray(body)) return body;
    if (Array.isArray(body?.results)) return body.results;
    if (Array.isArray(body?.appointments)) return body.appointments;
    return [];
};

/** Normalize client detail payloads from /clients/:id/ */
const unwrapClient = (payload) => payload?.data ?? payload ?? null;

/** Ignore stale responses from a previous chat */
const isStaleClient = (state, clientId) =>
    clientId != null &&
    state.activeClientId != null &&
    String(clientId) !== String(state.activeClientId);

export const WhatsappClientPanelSlice = createSlice({
    name: "whatsappClientPanel",
    initialState: {...WhatsappClientPanelInitialState},
    reducers: {
        waPanelClientRequesting: (state, action) => {
            state.activeClientId = action.payload?.clientId ?? null;
            state.client.isLoading = true;
            state.client.error = null;
            state.client.data = null;
        },
        waPanelClientSuccess: (state, action) => {
            const {clientId, data} = action.payload || {};
            if (isStaleClient(state, clientId)) return;
            state.client.isLoading = false;
            state.client.data = data;
            state.client.error = null;
        },
        waPanelClientError: (state, action) => {
            const {clientId, error} = action.payload || {};
            if (isStaleClient(state, clientId)) return;
            state.client.isLoading = false;
            state.client.error = error;
        },

        waPanelNotesRequesting: (state) => {
            state.notes.isLoading = true;
            state.notes.error = null;
        },
        waPanelNotesSuccess: (state, action) => {
            const {clientId, data} = action.payload || {};
            if (isStaleClient(state, clientId)) return;
            state.notes.isLoading = false;
            state.notes.data = data || [];
            state.notes.error = null;
        },
        waPanelNotesError: (state, action) => {
            const {clientId, error} = action.payload || {};
            if (isStaleClient(state, clientId)) return;
            state.notes.isLoading = false;
            state.notes.error = error;
        },

        waPanelTasksRequesting: (state) => {
            state.tasks.isLoading = true;
            state.tasks.error = null;
        },
        waPanelTasksSuccess: (state, action) => {
            const {clientId, data} = action.payload || {};
            if (isStaleClient(state, clientId)) return;
            state.tasks.isLoading = false;
            state.tasks.data = data || [];
            state.tasks.error = null;
        },
        waPanelTasksError: (state, action) => {
            const {clientId, error} = action.payload || {};
            if (isStaleClient(state, clientId)) return;
            state.tasks.isLoading = false;
            state.tasks.error = error;
        },

        waPanelAppointmentsRequesting: (state) => {
            state.appointments.isLoading = true;
            state.appointments.error = null;
        },
        waPanelAppointmentsSuccess: (state, action) => {
            const {clientId, data} = action.payload || {};
            if (isStaleClient(state, clientId)) return;
            state.appointments.isLoading = false;
            state.appointments.data = data || [];
            state.appointments.error = null;
        },
        waPanelAppointmentsError: (state, action) => {
            const {clientId, error} = action.payload || {};
            if (isStaleClient(state, clientId)) return;
            state.appointments.isLoading = false;
            state.appointments.error = error;
        },

        waPanelNoteCreateRequesting: (state) => {
            state.noteCreate.isLoading = true;
            state.noteCreate.error = null;
        },
        waPanelNoteCreateSuccess: (state, action) => {
            state.noteCreate.isLoading = false;
            state.noteCreate.error = null;
            const note = action.payload;
            if (note?.id) {
                state.notes.data = [
                    note,
                    ...(state.notes.data || []).filter((n) => n.id !== note.id),
                ];
            }
        },
        waPanelNoteCreateError: (state, action) => {
            state.noteCreate.isLoading = false;
            state.noteCreate.error = action.payload;
        },

        /** Full panel reset when closing or switching chats */
        whatsappClientPanelReset: () => ({...WhatsappClientPanelInitialState}),
    },
});

export const {
    waPanelClientRequesting,
    waPanelClientSuccess,
    waPanelClientError,
    waPanelNotesRequesting,
    waPanelNotesSuccess,
    waPanelNotesError,
    waPanelTasksRequesting,
    waPanelTasksSuccess,
    waPanelTasksError,
    waPanelAppointmentsRequesting,
    waPanelAppointmentsSuccess,
    waPanelAppointmentsError,
    waPanelNoteCreateRequesting,
    waPanelNoteCreateSuccess,
    waPanelNoteCreateError,
    whatsappClientPanelReset,
} = WhatsappClientPanelSlice.actions;

export const WhatsappClientPanelState = (state) => state.whatsappClientPanel;

/** GET /clients/:id/ — panel-only client fetch */
export const fetchWhatsappPanelClientAPI =
    (clientId) => async (dispatch) => {
        if (!clientId) return null;
        dispatch(waPanelClientRequesting({clientId}));
        try {
            const response = await apiService.get(
                apiUrlBuilder(endPoints.client.detail, {id: clientId}),
                {}
            );
            const data = unwrapClient(response.data);
            dispatch(waPanelClientSuccess({clientId, data}));
            return data;
        } catch (error) {
            dispatch(
                waPanelClientError({
                    clientId,
                    error: formatAxiosErrorMessage(error),
                })
            );
            return null;
        }
    };

/** GET /task/?client=&due__isnull=true — panel notes */
export const fetchWhatsappPanelNotesAPI =
    (clientId, {limit = 20} = {}) =>
    async (dispatch) => {
        if (!clientId) return null;
        dispatch(waPanelNotesRequesting());
        try {
            const stringQuery = qargsToQstring({
                client: clientId,
                due__isnull: true,
                ordering: "-created_on",
                limit,
                offset: 0,
            });
            const response = await apiService.get(
                `${endPoints.task.list}${stringQuery}`,
                {}
            );
            const data = unwrapList(response.data);
            dispatch(waPanelNotesSuccess({clientId, data}));
            return data;
        } catch (error) {
            dispatch(
                waPanelNotesError({
                    clientId,
                    error: formatAxiosErrorMessage(error),
                })
            );
            return null;
        }
    };

/**
 * GET /task/?client=&due__isnull=false&status=pending&due_or_null_after=today
 * Upcoming tasks with due dates for the panel.
 */
export const fetchWhatsappPanelTasksAPI =
    (clientId, {limit = 5} = {}) =>
    async (dispatch) => {
        if (!clientId) return null;
        dispatch(waPanelTasksRequesting());
        try {
            const dueAfter = new Date().toISOString().split("T")[0];
            const stringQuery = qargsToQstring({
                client: clientId,
                status: "pending",
                due_or_null_after: dueAfter,
                due__isnull: false,
                ordering: "due",
                limit,
                offset: 0,
            });
            const response = await apiService.get(
                `${endPoints.task.list}${stringQuery}`,
                {}
            );
            const data = unwrapList(response.data).slice(0, limit);
            dispatch(waPanelTasksSuccess({clientId, data}));
            return data;
        } catch (error) {
            dispatch(
                waPanelTasksError({
                    clientId,
                    error: formatAxiosErrorMessage(error),
                })
            );
            return null;
        }
    };

/**
 * GET /appointments/?client=&start_time_after=today
 * Upcoming appointments for the panel.
 */
export const fetchWhatsappPanelAppointmentsAPI =
    (clientId, {limit = 3} = {}) =>
    async (dispatch) => {
        if (!clientId) return null;
        dispatch(waPanelAppointmentsRequesting());
        try {
            const startAfter = new Date().toISOString().split("T")[0];
            const stringQuery = qargsToQstring({
                client: clientId,
                start_time_after: startAfter,
                ordering: "start_time",
                limit,
                offset: 0,
            });
            const response = await apiService.get(
                `${endPoints.lead.calendarAppointment}${stringQuery}`,
                {}
            );
            const data = unwrapList(response.data)
                .filter((a) => a && a.cancelled !== true)
                .slice(0, limit);
            dispatch(waPanelAppointmentsSuccess({clientId, data}));
            return data;
        } catch (error) {
            dispatch(
                waPanelAppointmentsError({
                    clientId,
                    error: formatAxiosErrorMessage(error),
                })
            );
            return null;
        }
    };

/** POST /task/ — create note and prepend into panel notes + global notes feed */
export const createWhatsappPanelNoteAPI =
    (payload) => async (dispatch, getState) => {
        dispatch(waPanelNoteCreateRequesting());
        try {
            const response = await apiService.post(endPoints.task.list, payload);
            const note = unwrapClient(response.data);
            dispatch(waPanelNoteCreateSuccess(note));
            // Mirror into All Notes list store so the left pane updates without refetch
            const panelClient = getState()?.whatsappClientPanel?.client?.data;
            const forList =
                note && typeof note.client === "object" && note.client?.id
                    ? note
                    : {
                          ...note,
                          client: panelClient || note?.client || null,
                      };
            dispatch(notesListPrependNote(forList));
            return note;
        } catch (error) {
            dispatch(
                waPanelNoteCreateError(formatAxiosErrorMessage(error))
            );
            return null;
        }
    };

export default WhatsappClientPanelSlice.reducer;
